import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { SignedSafetyControlRecord } from '../../services/reportsApi';
import { REPORT_VOCABULARY_BASIS } from './vocabularyBasis';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import { useSiteTimeZone } from '../../hooks/useSiteTimeZone';
import { resolveConfiguredZoneLabel } from '../../domain/customerLabels/customerFacingControlLabel';

function formatSignedAt(value: string | null | undefined, timeZone?: string | null): string | null {
  if (!value) return null;
  return formatSiteDateTime(value, timeZone) || value;
}

function displayValue(value: unknown, fallback = 'Not retained'): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
}

function formatRate(value: number | null): string {
  return value == null ? 'Not retained' : value.toFixed(2);
}

function findingSummaryLine(finding: SignedSafetyControlRecord['signed_body']['finding']): string {
  if (finding.reviewed_episodes != null || finding.rate_per_usable_hour != null) {
    return `${displayValue(finding.reviewed_episodes)} Reviewed episodes · ${displayValue(finding.usable_hours)} Usable hours · ${formatRate(finding.rate_per_usable_hour)} Rate per usable hour`;
  }
  const operational = finding.operational_observation;
  if (operational) {
    const parts = [
      operational.confirmed_episodes != null
        ? `${operational.confirmed_episodes} confirmed episodes (operational)`
        : null,
      operational.monitored_hours != null
        ? `${operational.monitored_hours} monitored hours (untrusted)`
        : null,
      finding.coverage_percent != null ? `${finding.coverage_percent}% coverage` : null,
    ].filter(Boolean);
    if (parts.length) {
      return `${parts.join(' · ')} · Claimable rate not retained`;
    }
  }
  return `${displayValue(finding.reviewed_episodes)} Reviewed episodes · ${displayValue(finding.usable_hours)} Usable hours · ${formatRate(finding.rate_per_usable_hour)} Rate per usable hour`;
}

function resultPresentation(record: SignedSafetyControlRecord) {
  const { control, recheck, result } = record.signed_body;
  if (recheck.status === 'not_planned') {
    return control.status === 'selected' || control.status === 'implemented' || control.status === 'verified'
      ? {
        headline: 'Control recorded',
        explanation: 'Comparable recheck pending.',
        showComparison: false,
        resultLabel: 'Recheck result' as const,
      }
      : {
        headline: 'Baseline established',
        explanation: 'Recheck not yet planned.',
        showComparison: false,
        resultLabel: 'Recheck result' as const,
      };
  }
  if (recheck.status === 'running') {
    return {
      headline: 'Recheck in progress',
      explanation: 'Recheck in progress.',
      showComparison: false,
      resultLabel: 'Recheck result' as const,
    };
  }
  if (recheck.status === 'not_comparable' || result.label === 'not_comparable') {
    return {
      headline: 'Recheck result: Not comparable',
      explanation: result.explanation || 'Comparability gate failed.',
      showComparison: true,
      resultLabel: 'Recheck result' as const,
    };
  }
  if (recheck.status === 'inconclusive' || result.label === 'inconclusive') {
    return {
      headline: 'Recheck result: Inconclusive',
      explanation: result.explanation || 'The comparable recheck does not support a directional result.',
      showComparison: true,
      resultLabel: 'Recheck result' as const,
    };
  }
  const comparableHeadline = result.label === 'decreased'
    ? result.headline
    : result.label === 'no_change'
      ? 'Verified result: No material change'
      : result.label === 'increased'
        ? 'Verified result: Recurrence increased'
        : null;
  return {
    headline: comparableHeadline || 'Recheck result pending',
    explanation: result.explanation || 'No directional conclusion is available.',
    showComparison: Boolean(comparableHeadline),
    // Claimable comparable directional outcomes are end-of-loop Verified result.
    resultLabel: comparableHeadline ? 'Verified result' as const : 'Recheck result' as const,
  };
}

export function CanonicalRecordHero({
  record,
  title,
  actions,
}: {
  record: SignedSafetyControlRecord;
  title: string;
  actions?: ReactNode;
}) {
  const {
    identity,
    governance,
    scope,
    finding,
    control,
    recheck,
    result,
    next_decision: nextDecision,
  } = record.signed_body;
  const siteId = typeof scope.site === 'string' ? scope.site : null;
  const zoneId = typeof scope.zone === 'string' ? scope.zone : null;
  const siteTimeZone = useSiteTimeZone(siteId);
  const zoneLabel = resolveConfiguredZoneLabel(zoneId) || zoneId;
  const presentation = resultPresentation(record);
  const showPercent = result.comparability_met
    && recheck.comparable
    && ['decreased', 'no_change', 'increased'].includes(result.label)
    && result.descriptive_percent_change != null;
  const decision = typeof nextDecision === 'string'
    ? nextDecision
    : nextDecision && typeof nextDecision === 'object' && !Array.isArray(nextDecision)
      ? displayValue(nextDecision.decision, 'No next decision retained')
      : 'No next decision retained';

  return (
    <Box
      component="header"
      id="summary"
      data-testid="canonical-record-hero"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2.5, md: 3 },
        bgcolor: '#081b33',
        color: '#f8fbff',
        borderTop: '5px solid #18bde3',
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 300,
          height: 300,
          right: -120,
          top: -190,
          borderRadius: '50%',
          border: '1px solid rgba(82, 211, 255, 0.25)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 3 }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
      >
        <Box sx={{ maxWidth: 900, minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ color: '#68ddff', letterSpacing: '0.18em', fontWeight: 800 }}
          >
            Edgentik · Signed Safety Control Record
          </Typography>
          <Typography
            variant="caption"
            data-testid="report-vocabulary-basis"
            title={REPORT_VOCABULARY_BASIS.detail}
            sx={{ display: 'block', mt: 0.25, color: 'rgba(248,251,255,0.72)' }}
          >
            {REPORT_VOCABULARY_BASIS.shortNote}
          </Typography>
          <Typography
            id="signed-record-title"
            component="h1"
            sx={{
              mt: 0.5,
              fontSize: { xs: '1.85rem', sm: '2.3rem', md: '2.75rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              fontWeight: 800,
            }}
          >
            {title}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
            <Chip
              label={identity.lifecycle_state === 'superseded' ? 'Superseded signed revision' : 'Signed'}
              sx={{ bgcolor: '#d9f7ff', color: '#081b33', fontWeight: 800 }}
            />
            <Chip
              variant="outlined"
              label={identity.provenance_label}
              sx={{ color: '#f8fbff', borderColor: 'rgba(248,251,255,0.38)' }}
            />
          </Stack>
        </Box>
        {actions ? <Box sx={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>{actions}</Box> : null}
      </Stack>

      <Box
        sx={{
          mt: { xs: 2, md: 2.25 },
          pt: 1.5,
          borderTop: '1px solid rgba(248,251,255,0.2)',
          display: 'grid',
          gap: { xs: 1.25, sm: 1.5 },
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(5, minmax(0, 1fr))',
          },
        }}
      >
        {[
          ['Signed revision', `Rev ${identity.revision}`],
          ['Signer', governance.signer_state !== 'legacy_unavailable'
            && governance.signer_name && governance.signer_role
            ? `${governance.signer_name} · ${governance.signer_role}`
            : governance.signer_label],
          ['Signed', formatSignedAt(governance.signed_at, siteTimeZone) || 'Timestamp unavailable'],
          ['Site', siteId || 'Not retained'],
          ['Zone', zoneLabel || 'Not retained'],
        ].map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ color: '#68ddff', textTransform: 'uppercase', letterSpacing: '0.1em' }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.84rem',
                overflowWrap: 'anywhere',
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {[
          ['Recurring pattern', findingSummaryLine(finding)],
          ['Control', displayValue(control.description, 'No control retained')],
          ['Comparable recheck', `${displayValue(recheck.reviewed_episodes)} Reviewed episodes · ${displayValue(recheck.usable_hours)} Usable hours · ${formatRate(recheck.rate_per_usable_hour)} Rate per usable hour`],
        ].map(([label, value]) => (
          <Paper
            key={label}
            variant="outlined"
            sx={{
              p: 1.25,
              bgcolor: 'rgba(255,255,255,0.06)',
              color: 'inherit',
              borderColor: 'rgba(248,251,255,0.2)',
            }}
          >
            <Typography variant="caption" sx={{ color: '#68ddff', fontWeight: 800 }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>{value}</Typography>
          </Paper>
        ))}
      </Box>

      <Box
        data-testid="canonical-result-summary"
        sx={{
          mt: 1,
          p: 1.5,
          bgcolor: '#f8fbff',
          color: '#081b33',
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(260px, 0.6fr)' },
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: '#087fa5', fontWeight: 800 }}>
            {presentation.resultLabel}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{presentation.headline}</Typography>
          <Typography variant="body2">{presentation.explanation}</Typography>
          {presentation.showComparison ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Baseline rate {formatRate(finding.rate_per_usable_hour)} · Recheck rate{' '}
              {formatRate(recheck.rate_per_usable_hour)} · Absolute rate difference{' '}
              {result.absolute_rate_change == null ? 'Not retained' : result.absolute_rate_change.toFixed(2)}
              {showPercent
                ? ` · Descriptive percentage change ${result.descriptive_percent_change!.toFixed(0)}%`
                : ''}
              {' '}· Comparability status {result.comparability_met ? 'Met' : 'Not met'}
            </Typography>
          ) : null}
          {presentation.showComparison ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>
              {displayValue(
                result.does_not_establish[0],
                'This comparison does not establish causation, incident prevention, or overall risk change.',
              )}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ borderLeft: { md: '1px solid rgba(8,27,51,0.18)' }, pl: { md: 1.5 } }}>
          <Typography variant="overline" color="text.secondary">Next decision</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{decision}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
